import { useState, useEffect, useCallback } from 'react';
import { challengeApi, ChallengeSummaryResponse } from '@/api/challenge';
import { refineErrorMessage } from '@/utils/errorMessages';

interface UseChallengeSummaryResult {
  data: ChallengeSummaryResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch challenge summary data for a completed challenge.
 *
 * Only works for completed challenges where the current user is a participant.
 * Returns error if challenge is not completed or user is not a participant.
 *
 * @param challengeId - Challenge ID
 * @param shouldFetch - Whether to fetch (set to true only when challenge is completed and user is participant)
 */
export function useChallengeSummary(
  challengeId: string | undefined,
  shouldFetch: boolean = true
): UseChallengeSummaryResult {
  const [data, setData] = useState<ChallengeSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    if (!challengeId || !shouldFetch) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await challengeApi.getSummary(challengeId);
      setData(response);
    } catch (err: any) {
      setError(refineErrorMessage(err));
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [challengeId, shouldFetch]);

  useEffect(() => {
    if (shouldFetch) {
      fetchSummary();
    }
  }, [fetchSummary, shouldFetch]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchSummary,
  };
}
